import * as THREE from 'three';
import { Consist } from './RollingStock.js';
import { box, weld, randInt, randRange } from '../level/VoxelFactory.js';
import {
  ROW, ROW_DEPTH, FIELD_HALF, WRAP_HALF, LANE, RAILCAR, FLATCAR, COLORS, JUICE,
} from '../core/Constants.js';

const SPAN = WRAP_HALF * 2;

function wrapX(v) {
  return ((((v + WRAP_HALF) % SPAN) + SPAN) % SPAN) - WRAP_HALF;
}

/** Signal mast geometry, built once and shared by every lane. */
let _mastGeo = null;
function mastGeometry() {
  if (!_mastGeo) {
    _mastGeo = weld([
      box(0.28, 2.6, 0.28, COLORS.CHASSIS, 0, 1.3, 0),
      box(0.7, 0.7, 0.5, COLORS.CHASSIS, 0, 2.75, 0),
    ]);
  }
  return _mastGeo;
}

let _lampGeo = null;
function lampGeometry() {
  if (!_lampGeo) _lampGeo = new THREE.BoxGeometry(0.42, 0.42, 0.2);
  return _lampGeo;
}

export class Lane {
  /**
   * @param {object} opts
   * @param {number} opts.row       lane index from the start bank
   * @param {string} opts.type      ROW.RAIL | ROW.FREIGHT
   * @param {number} opts.dir       +1 or -1
   * @param {number} opts.speed     world units / second
   * @param {number} opts.consists  how many consists share this lane
   * @param {number} opts.cars      cars per consist
   */
  constructor(opts, deps) {
    Object.assign(this, opts);
    this.z = -this.row * ROW_DEPTH;
    this.offset = 0;
    this.group = new THREE.Group();
    this.group.position.z = this.z;

    this.spacing = SPAN / this.consists;
    this.items = [];

    for (let i = 0; i < this.consists; i++) {
      const consist = new Consist(
        this.type === ROW.FREIGHT
          ? deps.flatcarGeometry
          : (lead) => deps.railcarGeometry(this.colorIndex, lead),
        deps.material,
        this.cars,
        this.type === ROW.FREIGHT,
        this.dir,
      );
      this.items.push(consist);
      this.group.add(consist.group);
    }

    this.buildSignals(deps);
    this.update(0);
  }

  buildSignals(deps) {
    this.lamps = [];
    if (this.type !== ROW.RAIL) return;
    // A mast on the side the trains arrive from, plus one opposite for symmetry.
    for (const side of [-1, 1]) {
      const mast = new THREE.Mesh(mastGeometry(), deps.scenery);
      mast.position.set(side * (FIELD_HALF + 1.6), 0, side * 0.9);
      this.group.add(mast);

      const lamp = new THREE.Mesh(lampGeometry(), deps.signalOff);
      lamp.position.set(side * (FIELD_HALF + 1.6), 2.75, side * 0.9 - side * 0.28);
      this.group.add(lamp);
      this.lamps.push(lamp);
    }
    this.signalOn = deps.signalOn;
    this.signalOff = deps.signalOff;
    this.lampLit = false;
  }

  update(dt, elapsed = 0) {
    this.offset += this.dir * this.speed * dt;
    for (let i = 0; i < this.items.length; i++) {
      if (!this.items[i].derailed) {
        this.items[i].setX(wrapX(this.offset + i * this.spacing));
      }
    }
    if (this.lamps && this.lamps.length) this.updateSignals(elapsed);
  }

  updateSignals(elapsed) {
    // Lit while any consist is about to enter, or is currently on, the field.
    const lead = this.speed * JUICE.SIGNAL_LEAD;
    let warn = false;
    for (const c of this.items) {
      if (c.derailed) continue;
      const entering = this.dir > 0
        ? c.max > -FIELD_HALF - lead && c.min < FIELD_HALF
        : c.min < FIELD_HALF + lead && c.max > -FIELD_HALF;
      if (entering) { warn = true; break; }
    }
    const blink = warn && Math.sin(elapsed * Math.PI * 2 * JUICE.SIGNAL_BLINK_HZ) > 0;
    if (blink !== this.lampLit) {
      this.lampLit = blink;
      const mat = blink ? this.signalOn : this.signalOff;
      for (const lamp of this.lamps) lamp.material = mat;
    }
  }

  /** RAIL lanes: is the player's box overlapping any car? */
  hits(x, halfWidth) {
    for (const c of this.items) {
      if (c.derailed) continue;
      if (x + halfWidth > c.min && x - halfWidth < c.max) return c;
    }
    return null;
  }

  /** FREIGHT lanes: which deck, if any, is under the player's feet? */
  carrierAt(x) {
    for (const c of this.items) {
      if (c.derailed) continue;
      if (x > c.min + 0.4 && x < c.max - 0.4) return c;
    }
    return null;
  }

  dispose() {
    this.group.removeFromParent();
    this.group.traverse((o) => { if (o.isMesh) o.geometry = null; });
    this.items.length = 0;
  }
}

/**
 * Pick a car count that still leaves a fair gap at this spacing, then build
 * the lane spec. Returns null-safe values — never produces overlapping cars.
 */
export function planLane(rng, { row, type, level }) {
  const isFreight = type === ROW.FREIGHT;
  const spec = isFreight ? FLATCAR : RAILCAR;

  const consists = randInt(rng, isFreight ? LANE.FREIGHT_CONSISTS : LANE.RAIL_CONSISTS);
  const spacing = SPAN / consists;

  const tighten = Math.min(level - 1, 6);
  const minGap = Math.max(
    LANE.MIN_GAP_FLOOR,
    (isFreight ? LANE.FREIGHT_MIN_GAP : LANE.RAIL_MIN_GAP) - tighten * 0.4,
  );

  // Largest consist that still leaves minGap of clear track.
  const maxCars = Math.max(
    1,
    Math.floor((spacing - minGap + spec.GAP) / (spec.LENGTH + spec.GAP)),
  );
  const wanted = randInt(rng, isFreight ? LANE.FREIGHT_CARS : LANE.RAIL_CARS);
  const cars = Math.min(wanted, maxCars);

  const base = Math.min(
    LANE.SPEED_CAP,
    LANE.BASE_SPEED + (level - 1) * LANE.SPEED_PER_LEVEL,
  );
  let speed = base * randRange(rng, LANE.SPEED_JITTER);
  if (isFreight) speed *= LANE.FREIGHT_SPEED_SCALE;

  return {
    row,
    type,
    dir: rng() < 0.5 ? -1 : 1,
    speed,
    consists,
    cars,
    colorIndex: Math.floor(rng() * COLORS.TRAIN.length),
  };
}
